import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "flex items-center gap-1.5 mr-1 text-xs text-gray-600 cursor-pointer select-none" }

import type { Chart } from 'highcharts';
import { computed, ref, toRef } from 'vue';
import { RotateCcw } from 'lucide-vue-next';
import GenericChart from '@/components/charts/GenericChart.vue';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { i18n } from '@/plugins/internationalization/i18n';
import type { SlaughterWeekBin } from '@/composables/records/useSlaughtersChartOptions';
import { useSlaughtersChartOptions } from '@/composables/records/useSlaughtersChartOptions';
import { clearHoverBand } from '@/composables/records/useRecordsChartOptions';

/*
 * Slaughters' own fork of `RecordsWeeklyChart`/`GroupedWeeklyChart`: one grouped pair of bars
 * per week - pig count always, discarded pig count only once the checkbox below is checked, on its
 * own y-axis. Click-to-select-a-week, fade-on-selection and the hover band behave the same as every
 * other records chart and are resolved the same way, in `useSlaughtersChartOptions`.
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'SlaughtersWeeklyChart',
  props: {
    bins: {},
    selectedWeekStart: {},
    pigsColor: {},
    discardedColor: {},
    title: {}
  },
  emits: ["week-selected", "selection-reset"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

// Off until the reader asks for it: discarded pigs are the smaller, secondary figure, and drawing
// them by default would compete with the pig-count bars this chart is opened to read first.
const showDiscarded = ref(false);

const pigsSeriesName = computed(() => i18n.global.t('views.pigs.records.chart.seriesName'));
const discardedSeriesName = computed(() => i18n.global.t('views.pigs.records.table.headings.discardedPigs'));

const options = useSlaughtersChartOptions(toRef(props, 'bins'), toRef(props, 'selectedWeekStart'), {
    pigsColor: toRef(props, 'pigsColor'),
    discardedColor: toRef(props, 'discardedColor'),
    title: toRef(props, 'title'),
    pigsSeriesName,
    discardedSeriesName,
    showDiscarded,
    onBinClicked: (weekStart: string) => emit('week-selected', weekStart),
});

function onChartUpdate(instance: Chart) {
    // A redraw drops imperatively added bands; the next pointer move puts a fresh one back.
    clearHoverBand(instance);
}

function onDiscardedToggle(checked: boolean | 'indeterminate') {
    showDiscarded.value = checked === true;
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(GenericChart, {
    options: (_unref(options) as any),
    onUpdate: onChartUpdate
  }, {
    "prepend-actions": _withCtx(() => [
      _createElementVNode("label", _hoisted_1, [
        _createVNode(_unref(Checkbox), {
          checked: showDiscarded.value,
          "onUpdate:checked": onDiscardedToggle
        }, null, 8, ["checked"]),
        _createTextVNode(" " + _toDisplayString(_ctx.$t('views.pigs.slaughters.chart.showDiscarded')), 1)
      ]),
      _createVNode(_unref(Button), {
        variant: "outline",
        size: "sm",
        disabled: _ctx.selectedWeekStart === null,
        "aria-label": _ctx.$t('views.pigs.records.chart.resetSelectionHint'),
        onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('selection-reset')))
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(RotateCcw), {
            class: "size-4 mr-1.5",
            "aria-hidden": "true"
          }),
          _createTextVNode(" " + _toDisplayString(_ctx.$t('views.pigs.records.chart.resetSelection')), 1)
        ]),
        _: 1
      }, 8, ["disabled", "aria-label"])
    ]),
    _: 1
  }, 8, ["options"]))
}
}

})